"use client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import React, { useEffect, useRef, useState } from "react";
import { type SubmitErrorHandler, useForm } from "react-hook-form";
import { useHomepageEntryVariant } from "@/components/homepage/HomepageEntryExperiment";
import { addMessage } from "@/components/Messages";
import { useMediaPreview } from "@/components/transcriptions/MediaPreview";
import { UploadDialogLanding } from "@/components/transcriptions/UploadDialogLanding";
import { Alert, AlertDescription } from "@/components/ui/alert";
import * as RadixForm from "@/components/ui/form";
import { useFeatureFlag } from "@/lib/feature-flags";
import { withHomepageEntryVariant } from "@/lib/homepage-entry-experiment";
import Link from "@/lib/locale/Link";
import { useRouter } from "@/lib/locale/navigation";
import { useUserData } from "@/lib/nhost/session-hooks";
import {
  getLandingUploadDimensions,
  resolveLandingUploadAttempt,
} from "@/lib/upload/landing-upload-attempt";
import {
  captureLandingUploadSignedInRedirect,
  captureLandingUploadValidationFailure,
} from "@/lib/upload/landing-upload-lifecycle";
import { useInsertTranscriptionJobLanding } from "@/lib/upload/useInsertTranscriptionJobLanding";
import { useMediaInputValidation } from "@/lib/upload/useMediaInputValidation";
import {
  AlignTextFormField,
  IsSongFormField,
  TargetLanguageFormField,
  TranscriptionTypeFormField,
} from "./UploadFormFields";
import { MediaFormFieldLanding } from "./UploadFormFieldsLanding";
import { getUploadFormSchema, type UploadFormData } from "./upload-form-schema";

const MAX_DURATION_S = 60 * 60 * 4; // 4 hours
const MAX_SIZE_MB = 40;
const ANONYMOUS_MINUTES = 10;

type LandingError = "credit_gate" | "existing_job" | "upload" | null;

export const UploadVideoLandingWrapperClient = () => {
  return <UploadVideoLanding />;
};

export const UploadVideoLanding = () => {
  const t = useTranslations();
  const router = useRouter();
  const user = useUserData();
  const homepageEntryVariant = useHomepageEntryVariant();
  const autoNavigateEnabled = useFeatureFlag("uploadAutoNavigate");
  const navigatedJobRef = useRef<string | null>(null);
  const submissionInFlightRef = useRef(false);
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const [uploadAlertOpen, setUploadAlertOpen] = useState(false);
  const [formMessage, setFormMessage] = useState("");
  const [uploadMessage, setUploadMessage] = useState("");
  const [landingError, setLandingError] = useState<LandingError>(null);
  const userIsFromAddToSongs = false;

  const form = useForm<UploadFormData>({
    defaultValues: {
      mediaInput: { files: [], youtubeUrl: "" },
      model: "large-v2",
      transcriptionType: "ELEVENLABS_SCRIBE",
      speakers: [{ name: "" }],
      text: "",
      targetLanguage: "",
      isSong: userIsFromAddToSongs,
    },
    resolver: zodResolver(getUploadFormSchema({ t })),
  });

  const isSong = form.watch("isSong");

  const urlFieldValue = form.watch("mediaInput.youtubeUrl");
  const { urlInfo, urlInfoLoading, setUrlInfo } = useMediaInputValidation({
    form,
    urlFieldValue,
    maxDurationS: MAX_DURATION_S,
    maxSizeMb: MAX_SIZE_MB,
  });

  const {
    captureLandingEvent,
    jobId,
    ownershipPersistence,
    resetActiveAttempt,
    setJobId,
    uploadStatus,
    setUploadStatus,
    handleInsertTranscriptionJob,
    uploader,
  } = useInsertTranscriptionJobLanding({
    setUploadMessage,
    setUploadAlertOpen,
  });

  const handleInvalidSubmit: SubmitErrorHandler<UploadFormData> = () => {
    captureLandingUploadValidationFailure(
      captureLandingEvent,
      getLandingUploadDimensions(form.getValues(), urlInfo)
    );
  };

  const handleSubmitForm = async (formData: UploadFormData) => {
    if (submissionInFlightRef.current) return;
    submissionInFlightRef.current = true;
    let returnsControlToForm = true;

    setFormMessage("");
    setLandingError(null);
    setUploadStatus("INITIAL");
    setUploadMessage("");

    const resolution = resolveLandingUploadAttempt({ formData, urlInfo });
    if (!resolution.ok) {
      captureLandingUploadValidationFailure(captureLandingEvent, resolution.dimensions);
      if (resolution.reason === "song_target_language") {
        form.setError("targetLanguage", {
          type: "manual",
          message: t("uploadForm.targetLanguageRequiredMessage"),
        });
      } else {
        setFormMessage(
          t(
            resolution.reason === "unsupported_url"
              ? "uploadForm.invalidUrlFileMessage"
              : "uploadForm.noInputErrorMessage"
          )
        );
      }
      submissionInFlightRef.current = false;
      return;
    }

    if (user && !user.isAnonymous) {
      captureLandingUploadSignedInRedirect(captureLandingEvent, resolution.attempt.dimensions);
      addMessage({
        type: "SUCCESS",
        text: t("landingUpload.alreadySignedInMessage"),
      });
      router.push("/app/dashboard");
      returnsControlToForm = false;
      return;
    }

    const durationMinutes = (urlInfo?.duration_s ?? duration ?? 0) / 60;
    const result = await handleInsertTranscriptionJob({
      attempt: resolution.attempt,
      hasCredits: durationMinutes <= ANONYMOUS_MINUTES,
    });

    if (result.status === "credit_gate") {
      setLandingError("credit_gate");
    } else if (result.status === "existing_anonymous_job") {
      setLandingError("existing_job");
    } else if (result.status === "failure") {
      setLandingError("upload");
    } else if (result.status === "success") {
      returnsControlToForm = false;
    }

    if (returnsControlToForm) submissionInFlightRef.current = false;
  };

  const autoNavigateJobId =
    uploadStatus === "SUCCESS" && (autoNavigateEnabled || ownershipPersistence === "claim_link")
      ? jobId
      : undefined;
  useEffect(() => {
    if (!autoNavigateJobId || navigatedJobRef.current === autoNavigateJobId) return;
    navigatedJobRef.current = autoNavigateJobId;
    router.replace(
      withHomepageEntryVariant(`/demo/transcriptions/${autoNavigateJobId}`, homepageEntryVariant)
    );
  }, [autoNavigateJobId, homepageEntryVariant, router]);

  const resetForm = () => {
    form.reset();
    setJobId("");
    setUploadMessage("");
    setUploadStatus("INITIAL");
    setFormMessage("");
    setLandingError(null);
    setUrlInfo(undefined);
    resetActiveAttempt();
    navigatedJobRef.current = null;
    submissionInFlightRef.current = false;
  };

  const isLoading = urlInfoLoading || uploadStatus === "LOADING";
  // Success navigates or resets through the fallback dialog. Errors leave the
  // native submit control enabled so focus and form values remain useful.
  const hasResult = uploadStatus === "SUCCESS";
  const canSubmit = !isLoading && !hasResult;

  useEffect(() => {
    if (landingError === "upload" && !isLoading) {
      submitButtonRef.current?.focus();
    }
  }, [isLoading, landingError]);

  const resetFormFile = () => {
    form.resetField("mediaInput.files");
  };

  const fileInputs = form.watch("mediaInput.files") as File[] | undefined;
  const fileInput = fileInputs?.[0];

  const mediaPreviewRef = useRef<HTMLVideoElement | HTMLAudioElement>(null);
  const mediaValue = (form.getValues("mediaInput.files")?.[0] ?? null) as File | null;
  const { duration, sizeMb, type, url } = useMediaPreview({
    file: fileInput,
  });

  useEffect(() => {
    if (sizeMb > MAX_SIZE_MB) {
      //setFormMessage(t("uploadForm.tooLargeFileMessage", { size: "2000MB" }));
      if (!form.getFieldState("mediaInput.files")?.error) {
        form.setError("mediaInput.files", {
          type: "manual",
          message: t("uploadForm.tooLargeFileMessage", {
            size: `${MAX_SIZE_MB}MB`,
          }),
        });
      }
    }
    if (duration > MAX_DURATION_S) {
      //setFormMessage(t("uploadForm.tooLongMediaMessage", { duration: "4" }));
      if (!form.getFieldState("mediaInput.files")?.error) {
        form.setError("mediaInput.files", {
          type: "manual",
          message: t("uploadForm.tooLongMediaMessage", {
            duration: `${Math.floor(MAX_DURATION_S / 3600)}`,
          }),
        });
      }
    }
  }, [sizeMb, duration]);

  useEffect(() => {
    if (urlInfo?.duration_s != null && urlInfo.duration_s > MAX_DURATION_S) {
      //setFormMessage(t("uploadForm.tooLongMediaMessage", { duration: "4" }));
      if (!form.getFieldState("mediaInput.youtubeUrl")?.error) {
        form.setError("mediaInput.youtubeUrl", {
          type: "manual",
          message: t("uploadForm.tooLongMediaMessage", {
            duration: `${Math.floor(MAX_DURATION_S / 3600)}`,
          }),
        });
      }
    }
  }, [urlInfo]);

  const availableTranscriptionTypes = [
    {
      key: "MODAL_ASR_AND_ALIGN",
      description: t("uploadForm.modalAsrAndAlignTranscriptionDescription"),
      label: t("uploadForm.modalAsrAndAlignTranscriptionLabel"),
    },
    {
      key: "FALAI_WIZPER",
      description: t("uploadForm.falaiWizperTranscriptionDescription"),
      label: t("uploadForm.falaiWizperTranscriptionLabel"),
    },
    {
      key: "ELEVENLABS_SCRIBE",
      description: t("uploadForm.elevenlabsTranscriptionDescription"),
      label: t("uploadForm.elevenlabsTranscriptionLabel"),
    },
  ];

  return (
    <div>
      <h2 className="mb-6 scroll-m-20 text-center font-bold text-2xl tracking-tight lg:text-3xl">
        {t("landingUpload.uploadFormHeader")}
      </h2>

      <div>
        <RadixForm.Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmitForm, handleInvalidSubmit)}
            className="space-y-8"
            id="start-transcription-form"
          >
            <MediaFormFieldLanding
              form={form}
              mediaPreviewRef={mediaPreviewRef}
              mediaValue={mediaValue}
              duration={duration}
              sizeMb={sizeMb}
              type={type}
              url={url}
              urlInfo={urlInfo}
              urlInfoLoading={urlInfoLoading}
              resetFormFile={resetFormFile}
              uploader={uploader}
              uploadStatus={uploadStatus}
              isLoading={isLoading}
              canSubmit={canSubmit}
              maxSizeMb={MAX_SIZE_MB}
              maxDurationS={MAX_DURATION_S}
              submitButtonRef={submitButtonRef}
            />
            {userIsFromAddToSongs && <IsSongFormField form={form} />}
            <AlignTextFormField form={form} isSong={isSong} />
            {!isSong && false && (
              <TranscriptionTypeFormField
                form={form}
                availableTranscriptionTypes={availableTranscriptionTypes}
              />
            )}
            <TargetLanguageFormField form={form} />
            <IsSongFormField form={form} />
            {formMessage && <RadixForm.FormMessage>{formMessage}</RadixForm.FormMessage>}
            {form.formState.errors.root && (
              <RadixForm.FormMessage>{form.formState.errors.root.message}</RadixForm.FormMessage>
            )}
            <div role="alert" aria-live="assertive" aria-atomic="true">
              {(landingError === "credit_gate" || landingError === "existing_job") && (
                <Alert role="presentation" variant="destructive">
                  <p className="font-medium">
                    {t(
                      landingError === "credit_gate"
                        ? "landingUpload.freeLimitTitle"
                        : "landingUpload.alreadyUploadedTitle"
                    )}
                  </p>
                  <AlertDescription className="mt-2 space-y-3">
                    <p>
                      {t(
                        landingError === "credit_gate"
                          ? "landingUpload.freeLimitDescription"
                          : "landingUpload.alreadyUploadedMessage"
                      )}
                    </p>
                    <Link
                      className="inline-block font-medium underline"
                      href={withHomepageEntryVariant("/app/auth/sign-up", homepageEntryVariant)}
                    >
                      {t("landingUpload.createAccountAction")}
                    </Link>
                  </AlertDescription>
                </Alert>
              )}
              {landingError === "upload" && (autoNavigateEnabled || !jobId) && (
                <Alert role="presentation" variant="destructive">
                  <p className="font-medium">{t("landingUpload.uploadFailedTitle")}</p>
                  <AlertDescription className="mt-2">
                    <p>{t("landingUpload.uploadFailedDescription")}</p>
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </form>
        </RadixForm.Form>
      </div>
      <div>
        {!autoNavigateEnabled && ownershipPersistence !== "claim_link" && jobId && (
          <UploadDialogLanding
            uploadAlertOpen={uploadAlertOpen}
            setUploadAlertOpen={setUploadAlertOpen}
            jobId={jobId}
            uploadStatus={uploadStatus}
            uploadMessage={uploadMessage}
            resetForm={resetForm}
            homepageEntryVariant={homepageEntryVariant}
          />
        )}
      </div>
    </div>
  );
};
